import { Button } from '@mui/material';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FormValues } from '.';
import { BankOperationActionsMode } from './BankOperationActions';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';

type Props = {
  vehicleId: string;
  bankAccountId: string;
  bankAccountOperationId: string;
  bankOperationAmountTcy: number;
  matchingReconcilableTransactions: Array<{
    id: string;
    counterpartyLabel: string;
    actionRef: ActionRef;
    currencyCode: CurrencyCode;
    operationType: ActionOperationType;
  }>;
};

const TransactionActionSubmit: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankAccountOperationId,
  bankOperationAmountTcy,
  matchingReconcilableTransactions,
}) => {
  const { handleSubmit: onSubmit, watch } = useFormContext<FormValues>();
  const mode = watch('mode');

  const reconcileOperation =
    api.bankAccount.reconcileBankAccountOperation.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Transaction reconciled.',
      }),
    );

  const createTransfer = api.bankAccount.createTransfer.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Transfer created.',
    }),
  );

  const handleSubmit = (values: FormValues) => {
    if (!bankOperationAmountTcy) return;

    if (values.mode === BankOperationActionsMode.QuickReconcile) {
      const transaction = matchingReconcilableTransactions.find(
        (transaction) => transaction.id === values.transactionId,
      );

      if (!transaction) return;

      reconcileOperation.mutate({
        vehicleId,
        bankAccountId,
        bankAccountOperationId,
        reconciledTransactions: [
          {
            id: values.transactionId,
            selectedAmount: bankOperationAmountTcy,
            operationType: transaction.operationType,
            currencyCode: transaction.currencyCode,
            actionRef: transaction.actionRef,
          },
        ],
      });
    } else if (values.mode === BankOperationActionsMode.CreateTransfer) {
      createTransfer.mutate({
        vehicleId,
        sourceAccountId: bankAccountId,
        destinationAccountId: values.targetBankAccountId,
        sourceBankOperationId: bankAccountOperationId,
        reference: values.reference,
        transactions: values.transactions,
        currencyRate: values.currencyRate,
      });
    }
  };

  const enabled =
    !!matchingReconcilableTransactions.length ||
    mode === BankOperationActionsMode.CreateTransfer;

  return (
    <Button
      onClick={onSubmit(handleSubmit)}
      variant="contained"
      size="large"
      disabled={
        !enabled || reconcileOperation.isPending || createTransfer.isPending
      }
    >
      Ok
    </Button>
  );
};

export default TransactionActionSubmit;
